import { Color } from '../types/color';

/**
 * isBoolean
 * Checks if a value is a boolean.
 * @param value
 * @returns
 */
export const isBoolean = (value: unknown): value is boolean => {
	return typeof value === 'boolean';
};

/**
 * isString
 * Checks if a value is a string.
 * @param value
 * @returns
 */
export const isString = (value: unknown): value is string => {
	return typeof value === 'string';
};

/**
 * isNumber
 * Checks if a value is a finite number.
 * @param value
 * @returns
 */
export const isNumber = (value: unknown): value is number => {
	return typeof value === 'number' && Number.isFinite(value);
};

/**
 * snakeCase
 * Converts a string to snake_case, stripping accented characters.
 * @param value
 * @returns
 */
export const snakeCase = (value: string): string => {
	return value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/([a-z])([A-Z])/g, '$1_$2')
		.replace(/[\s\-]+/g, '_')
		.toLowerCase();
};

const COLOR_VALUES = new Set<string>(Object.values(Color));

const CSS_COLOR_PATTERNS = [
	/^#(?:[\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i,
	/^rgb\(\s*(?:\d{1,3}%?\s*,\s*){2}\d{1,3}%?\s*\)$/i,
	/^rgba\(\s*(?:\d{1,3}%?\s*,\s*){3}(?:0|1|0?\.\d+)\s*\)$/i,
	/^hsl\(\s*-?\d+(?:deg|rad|grad|turn)?\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*\)$/i,
	/^hsla\(\s*-?\d+(?:deg|rad|grad|turn)?\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*,\s*(?:0|1|0?\.\d+)\s*\)$/i,
	/^var\(\s*--[\w-]+(?:\s*,\s*.+)?\)$/i,
];

/**
 * isColor
 * Checks if a value is a Color token or a valid CSS color string.
 * @param value
 * @returns
 */
export const isColor = (value: unknown): value is string => {
	if (!isString(value)) return false;

	const color = value.trim();
	if (!color) return false;

	return (
		COLOR_VALUES.has(color) ||
		CSS_COLOR_PATTERNS.some((pattern) => pattern.test(color))
	);
};
